import { useEditUser, useResetUserPassword } from "@/api/mutations/users";
import { Button } from "@/components/ui/button";
import { Clipboard } from "@/components/ui/clipboard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface ResetPasswordDialogProps {
  userId: number;
}

export const ResetPasswordDialog = ({ userId }: ResetPasswordDialogProps) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const { mutateAsync: editUserMutation, isPending: isEditing } = useEditUser(userId);
  const { mutateAsync: resetPasswordMutation, isPending: isResetting } =
    useResetUserPassword(userId);
  const [newPassword, setNewPassword] = useState<string | undefined>(undefined);

  const loading = isEditing || isResetting;

  const handleReset = async () => {
    if (!password.length) {
      console.log(123);
      return resetPasswordMutation()
        .then((res) => {
          setNewPassword(res.temporaryPassword);
          toast.success("Password reset successfully");
        })
        .catch(() => {
          toast.error("Failed to reset password");
        });
    }

    editUserMutation({
      password,
    })
      .then(() => {
        toast.success("Password changed successfully");
      })
      .catch(() => {
        toast.error("Failed to change password");
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button variant="outline" size="sm" data-testid="reset-password-btn">
          Reset Password
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            {newPassword ? (
              <div className="space-y-2 w-full">
                <p className="text-sm font-medium text-foreground">New Password</p>
                <Clipboard text={newPassword} data-testid="clipboard-component" />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setNewPassword(undefined);
                    setPassword("");
                  }}
                >
                  Back to Input
                </Button>
              </div>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      You can leave the password input empty and server will generate the new
                      password automatically
                    </p>
                  </TooltipContent>
                </Tooltip>
                <Input
                  className="w-full"
                  type="password"
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </>
            )}
          </div>

          <Button
            data-testid="submit-btn"
            className="w-full"
            onClick={handleReset}
            disabled={loading}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
